// @ts-nocheck
import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/hooks/use-auth";
import { usePermissions } from "@/hooks/use-permissions";
import { User, Plus, X, ShieldAlert } from "lucide-react";
import { MAX_COMMISSION_PERCENTAGE } from "@/lib/commission-validation";

// ─── Types ────────────────────────────────────────────────────────────────────

interface RoleConfig {
  role: string;
  label: string;
  testId: string;
  roleType?: string;
}

interface APIRole {
  id: string;
  name: string;
  type: string;
  description: string | null;
}

const PA_ENUM_TYPES = new Set(["sales_pa", "field_pa", "desk_pa", "consultant"]);

interface BaseProps {
  adjusters: any[];
  roles?: RoleConfig[];
  title?: string;
  description?: string;
  className?: string;
  layout?: string;
  bare?: boolean;
  /** The claim's state (code or name). Adjusters whose license for it has lapsed cannot be picked. */
  claimState?: string | null;
}

interface MutationModeProps extends BaseProps {
  mode?: "mutation";
  paAssignments: any[];
  updatePaAssignmentMutation: any;
  createPaAssignmentMutation: any;
  deletePaAssignmentMutation: any;
  assignments?: never;
  onAssignmentsChange?: never;
}

interface FormModeProps extends BaseProps {
  mode: "form";
  assignments: any[];
  onAssignmentsChange: (assignments: any[]) => void;
  onDynamicRolesChange?: (roles: RoleConfig[]) => void;
  initialDynamicRoles?: RoleConfig[];
  paAssignments?: never;
  updatePaAssignmentMutation?: never;
  createPaAssignmentMutation?: never;
  deletePaAssignmentMutation?: never;
}

type QuickAssignCardProps = MutationModeProps | FormModeProps;

interface LapsedLicense {
  userId: string;
  adjusterName: string;
  state: string;
  expiredOn: string | null;
}

const US_STATE_CODE = /^[A-Za-z]{2}$/;

function stateCodeOf(state: string | null | undefined): string | null {
  const trimmed = state?.trim();
  return trimmed && US_STATE_CODE.test(trimmed) ? trimmed.toUpperCase() : null;
}

function lapseText(lapse: LapsedLicense): string {
  return `${lapse.state} license ${lapse.expiredOn ? `expired ${lapse.expiredOn}` : "expired"}`;
}

function adjusterDisplayName(adjuster: any): string {
  if (adjuster.firstName || adjuster.lastName) {
    return `${adjuster.firstName || ""} ${adjuster.lastName || ""}`.trim();
  }
  return adjuster.name || "Unknown";
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function QuickAssignCard(props: QuickAssignCardProps) {
  const {
    title = "Quick Assign",
    description = "Assign adjusters by role",
    className = "bg-green-50 dark:bg-green-950/20",
    bare = false,
  } = props;

  const content = <UnifiedCardContent {...props} />;

  if (bare) return content;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center text-base">
          <User className="h-5 w-5 mr-2" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}

// ─── Unified Mode ────────────────────────────────────────────────────────────

function UnifiedCardContent(props: QuickAssignCardProps) {
  const {
    adjusters,
    roles: staticRoles,
    layout = "grid-cols-1",
  } = props;

  const isFormMode = props.mode === "form";
  const paAssignments = isFormMode ? (props.assignments || []) : (props.paAssignments || []);

  const { user, isAuthenticated } = useAuth();
  const { canAssignClaims } = usePermissions();
  const claimStateCode = stateCodeOf(props.claimState);
  const { data: lapsedLicenses = [] } = useQuery<LapsedLicense[]>({
    queryKey: ["/api/license-compliance/lapsed"],
    enabled: isAuthenticated && !!claimStateCode,
  });
  const lapsedInClaimState = useMemo(() => {
    const byUser = new Map<string, LapsedLicense>();
    if (!claimStateCode) return byUser;
    for (const lapse of lapsedLicenses) {
      if (stateCodeOf(lapse.state) === claimStateCode) byUser.set(lapse.userId, lapse);
    }
    return byUser;
  }, [lapsedLicenses, claimStateCode]);
  const currentUserId = user?.id;
  const userRoleType = (user as any)?.roleType;
  const isAdmin = user?.role === 'admin' || userRoleType === 'owner' || userRoleType === 'admin' || userRoleType === 'office_manager';
  const canViewAll = isAdmin || userRoleType === 'accountant';
  const { data: apiRoles = [] } = useQuery<APIRole[]>({
    queryKey: ["/api/roles"],
    enabled: isAuthenticated,
  });

  const [extraSlots, setExtraSlots] = useState<Record<string, number>>({});
  const [percentageEdits, setPercentageEdits] = useState<Record<string, string>>({});
  const [feeTypeEdits, setFeeTypeEdits] = useState<Record<string, string>>({});
  const [newSlotPercentages, setNewSlotPercentages] = useState<Record<string, string>>({});
  const [newSlotFeeTypes, setNewSlotFeeTypes] = useState<Record<string, string>>({});

  // Dynamic initialization for form mode initial roles
  const normalizedInitialRoles = useMemo(() => {
    if (!isFormMode || !props.initialDynamicRoles) return [];
    return props.initialDynamicRoles.map((r) => {
      const matchedApiRole = apiRoles.find(
        (ar) => ar.type === r.roleType || 
                ar.type === r.roleType?.toLowerCase() ||
                ar.name.toLowerCase() === r.label?.toLowerCase() ||
                ar.name.toLowerCase() === r.roleType?.toLowerCase().replace(/_/g, ' ')
      );
      if (matchedApiRole) {
        return {
          ...r,
          role: r.role || matchedApiRole.type,
          roleType: matchedApiRole.type,
          label: matchedApiRole.name,
          testId: `qa-select-${matchedApiRole.type}`,
        };
      }
      return r;
    });
  }, [isFormMode, props.initialDynamicRoles, apiRoles]);

  const [addedRoleSections, setAddedRoleSections] = useState<RoleConfig[]>(normalizedInitialRoles);

  useEffect(() => {
    if (isFormMode && normalizedInitialRoles.length > 0 && apiRoles.length > 0) {
      setAddedRoleSections(normalizedInitialRoles);
    }
  }, [normalizedInitialRoles, apiRoles.length, isFormMode]);

  const handleAddedRolesChange = (newRoles: RoleConfig[]) => {
    setAddedRoleSections(newRoles);
    if (isFormMode && props.onDynamicRolesChange) {
      props.onDynamicRolesChange(newRoles);
    }
  };

  const defaultRoles: RoleConfig[] = useMemo(() => staticRoles || [
    { role: "sales_pa", label: "Sales PA", testId: "qa-select-sales-pa", roleType: "sales_pa" },
    { role: "field_pa", label: "Field PA", testId: "qa-select-field-pa", roleType: "field_pa" },
    { role: "desk_pa", label: "Desk PA", testId: "qa-select-desk-pa", roleType: "desk_pa" },
  ], [staticRoles]);

  const existingAssignmentRoles = useMemo(() => {
    const defaultTypes = new Set(defaultRoles.map((r) => r.roleType || r.role));
    const addedTypes = new Set(addedRoleSections.map((r) => r.roleType || r.role));
    const extra: RoleConfig[] = [];
    const seenKeys = new Set<string>();
    (paAssignments || []).forEach((a: any) => {
      if (!a.isActive && !isFormMode) return;
      const roleKey = a.role === "other" ? (a.customRoleName || "other") : a.role;
      if (defaultTypes.has(roleKey) || seenKeys.has(roleKey)) return;
      const roleName = a.role === "other" ? a.customRoleName || "Other" : apiRoles.find((r) => r.type === a.role)?.name || a.role.replace(/_/g, " ");
      const matchedApiRole = a.role === "other" && a.customRoleName ? apiRoles.find((r) => r.name === a.customRoleName) : null;
      const resolvedRoleType = matchedApiRole?.type || roleKey;
      if (addedTypes.has(roleKey) || addedTypes.has(resolvedRoleType)) return;
      seenKeys.add(roleKey);
      extra.push({
        role: roleKey,
        label: roleName,
        testId: `qa-select-${resolvedRoleType}`,
        roleType: resolvedRoleType,
      });
    });
    return extra;
  }, [paAssignments, apiRoles, defaultRoles, addedRoleSections, isFormMode]);

  const activeRoles = useMemo(() => {
    const all = [...defaultRoles, ...existingAssignmentRoles, ...addedRoleSections];
    const seen = new Set<string>();
    return all.filter((r) => {
      const key = r.roleType === 'other' ? `other-${r.label}` : (r.roleType || r.role);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [defaultRoles, existingAssignmentRoles, addedRoleSections]);

  const lapsedAssignees = useMemo(() => {
    const assigned = new Set((paAssignments || []).filter((a: any) => (a.isActive || isFormMode) && a.userId).map((a: any) => a.userId));
    return Array.from(lapsedInClaimState.values()).filter((lapse) => assigned.has(lapse.userId));
  }, [paAssignments, lapsedInClaimState, isFormMode]);

  const usedRoleTypes = new Set(activeRoles.map((r) => r.roleType || r.role));
  const availableToAdd = apiRoles.filter((r) => !usedRoleTypes.has(r.type));

  const getPaAssignmentsByRole = (role: string, label?: string) => {
    return (paAssignments || []).filter((a: any) => {
      if (!a.isActive && !isFormMode) return false;
      if (PA_ENUM_TYPES.has(role)) return a.role === role;
      if (a.role === "other") return a.customRoleName === (label || role);
      return a.role === role;
    });
  };

  const computeTotalPercentage = () => {
    let total = 0;
    for (const a of paAssignments || []) {
      if (!a.isActive && !isFormMode) continue;
      const feeType = feeTypeEdits[a.id] || a.commissionFeeType || 'percent';
      if (feeType !== 'percent') continue;
      const edited = percentageEdits[a.id];
      const val = edited !== undefined ? parseFloat(edited) : parseFloat(a.commissionPercentage || "0");
      total += isNaN(val) ? 0 : val;
    }
    for (const [key, val] of Object.entries(newSlotPercentages)) {
      const ft = newSlotFeeTypes[key] || 'percent';
      if (ft !== 'percent') continue;
      total += parseFloat(val) || 0;
    }
    return total;
  };
  const totalPercentage = computeTotalPercentage();
  const exceedsLimit = totalPercentage > MAX_COMMISSION_PERCENTAGE;

  const handlePercentageCommit = (assignmentId: string, role: string) => {
    const raw = percentageEdits[assignmentId];
    if (raw === undefined && feeTypeEdits[assignmentId] === undefined) return;
    
    const assignment = (paAssignments || []).find((a: any) => a.id === assignmentId);
    if (!assignment) return;

    const feeType = feeTypeEdits[assignmentId] || assignment.commissionFeeType || 'percent';
    const rawValue = raw !== undefined ? parseFloat(raw) : parseFloat(assignment.commissionPercentage || '0');
    const value = isNaN(rawValue) ? 0 : rawValue;
    if (value < 0) return;
    if (feeType === 'percent' && value > MAX_COMMISSION_PERCENTAGE) return;
    if (feeType === 'percent' && computeTotalPercentage() > MAX_COMMISSION_PERCENTAGE) return;

    const mutateData: any = { role, userId: assignment.userId, assignmentId, commissionPercentage: feeType === 'percent' ? value.toString() : '0', commissionFeeType: feeType };
    if (feeType === 'flat_rate') mutateData.commissionFlatAmount = value.toString();
    if (feeType === 'hourly') mutateData.commissionHourlyRate = value.toString();
    
    if (isFormMode) {
      const next = [...paAssignments];
      const idx = next.findIndex((a: any) => a.id === assignmentId);
      if (idx !== -1) {
        next[idx] = { ...next[idx], ...mutateData };
        props.onAssignmentsChange(next);
      }
    } else {
      props.updatePaAssignmentMutation.mutate({
        data: mutateData,
        paAssignments: paAssignments || [],
      });
    }
    
    setPercentageEdits((prev) => { const next = { ...prev }; delete next[assignmentId]; return next; });
    setFeeTypeEdits((prev) => { const next = { ...prev }; delete next[assignmentId]; return next; });
  };

  const handleAddRoleSection = (roleId: string) => {
    const apiRole = apiRoles.find((r) => r.id === roleId);
    if (!apiRole) return;
    handleAddedRolesChange([
      ...addedRoleSections,
      {
        role: apiRole.type,
        label: apiRole.name,
        testId: `qa-select-${apiRole.type}`,
        roleType: apiRole.type,
      },
    ]);
  };

  const renderDropdownRow = (
    assignment: any | null,
    role: string,
    label: string,
    testId: string,
    idx: number,
    isExtraSlot = false,
    slotIdx?: number,
    roleType?: string,
    customRoleName?: string,
    readOnly = false,
  ) => {
    const slotKey = roleType || role;
    const defaultSlotKey = `${slotKey}-default`;
    const key = assignment?.id ?? `extra-${slotKey}-${slotIdx}`;
    const currentFeeType = assignment
      ? feeTypeEdits[assignment.id] || assignment.commissionFeeType || 'percent'
      : isExtraSlot
      ? newSlotFeeTypes[`${slotKey}-${slotIdx}`] || 'percent'
      : newSlotFeeTypes[defaultSlotKey] || 'percent';
    const currentValue = assignment
      ? percentageEdits[assignment.id] ?? (
          currentFeeType === 'flat_rate' ? parseFloat(assignment.commissionFlatAmount || "0").toString()
          : currentFeeType === 'hourly' ? parseFloat(assignment.commissionHourlyRate || "0").toString()
          : parseFloat(assignment.commissionPercentage || "0").toString()
        )
      : isExtraSlot
      ? newSlotPercentages[`${slotKey}-${slotIdx}`] ?? "0"
      : newSlotPercentages[defaultSlotKey] ?? "0";

    const effectiveRole = PA_ENUM_TYPES.has(roleType || role) ? (roleType || role) : "other";
    const effectiveCustomName = effectiveRole === "other" ? (customRoleName || label) : undefined;

    const assignedUserIds = new Set(
      getPaAssignmentsByRole(role, customRoleName || label)
        .map((a: any) => a.userId)
        .filter(Boolean)
    );

    return (
      <div key={key} className="flex flex-wrap items-center gap-1">
        <Select
          value={assignment?.userId || "none"}
          onValueChange={(value) => {
            if (readOnly) return;
            if (isExtraSlot) {
              if (value !== "none") {
                const pct = newSlotPercentages[`${slotKey}-${slotIdx}`] || "0";
                const ft = newSlotFeeTypes[`${slotKey}-${slotIdx}`] || 'percent';
                const mutateData: any = { role: effectiveRole, userId: value, commissionPercentage: ft === 'percent' ? pct : '0', commissionFeeType: ft };
                if (ft === 'flat_rate') mutateData.commissionFlatAmount = pct;
                if (ft === 'hourly') mutateData.commissionHourlyRate = pct;
                if (effectiveCustomName) mutateData.customRoleName = effectiveCustomName;

                if (isFormMode) {
                  const newAssignment = { id: `form-${Date.now()}-${Math.random()}`, ...mutateData };
                  props.onAssignmentsChange([...paAssignments, newAssignment]);
                  setExtraSlots((prev) => ({ ...prev, [slotKey]: Math.max(0, (prev[slotKey] || 0) - 1) }));
                  setNewSlotPercentages((prev) => { const next = { ...prev }; delete next[`${slotKey}-${slotIdx}`]; return next; });
                } else {
                  props.createPaAssignmentMutation.mutate(mutateData, {
                    onSuccess: () => {
                      setExtraSlots((prev) => ({ ...prev, [slotKey]: Math.max(0, (prev[slotKey] || 0) - 1) }));
                      setNewSlotPercentages((prev) => { const next = { ...prev }; delete next[`${slotKey}-${slotIdx}`]; return next; });
                    },
                  });
                }
              }
            } else if (assignment) {
              if (value === "none" && isFormMode) {
                // If they set to none in form mode, remove it natively
                props.onAssignmentsChange(paAssignments.filter((a: any) => a.id !== assignment.id));
              } else if (isFormMode) {
                props.onAssignmentsChange(paAssignments.map((a: any) => 
                  a.id === assignment.id ? { ...a, userId: value } : a
                ));
              } else {
                props.updatePaAssignmentMutation.mutate({
                  data: { role: effectiveRole, userId: value === "none" ? null : value, assignmentId: assignment.id },
                  paAssignments: paAssignments || [],
                });
              }
            } else {
              if (value !== "none") {
                const pct = newSlotPercentages[defaultSlotKey] || "0";
                const ft = newSlotFeeTypes[defaultSlotKey] || 'percent';
                const mutateData: any = { role: effectiveRole, userId: value, commissionPercentage: ft === 'percent' ? pct : '0', commissionFeeType: ft };
                if (ft === 'flat_rate') mutateData.commissionFlatAmount = pct;
                if (ft === 'hourly') mutateData.commissionHourlyRate = pct;
                if (effectiveCustomName) mutateData.customRoleName = effectiveCustomName;
                
                if (isFormMode) {
                  const newAssignment = { id: `form-${Date.now()}-${Math.random()}`, ...mutateData };
                  props.onAssignmentsChange([...paAssignments, newAssignment]);
                  setNewSlotFeeTypes((prev) => { const next = { ...prev }; delete next[defaultSlotKey]; return next; });
                  setNewSlotPercentages((prev) => { const next = { ...prev }; delete next[defaultSlotKey]; return next; });
                } else {
                  props.createPaAssignmentMutation.mutate(mutateData, {
                    onSuccess: () => {
                      setNewSlotFeeTypes((prev) => { const next = { ...prev }; delete next[defaultSlotKey]; return next; });
                      setNewSlotPercentages((prev) => { const next = { ...prev }; delete next[defaultSlotKey]; return next; });
                    },
                  });
                }
              }
            }
          }}
          disabled={readOnly || (!isFormMode && (isExtraSlot ? props.createPaAssignmentMutation.isPending : props.updatePaAssignmentMutation?.isPending))}
        >
          <SelectTrigger
            data-testid={isExtraSlot ? `${testId}-new-${slotIdx}` : idx === 0 ? testId : `${testId}-${idx}`}
            className="bg-card dark:bg-muted min-w-0 flex-1"
          >
            <SelectValue placeholder={`Select ${label}`} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {(adjusters ?? [])
              .filter((a: any) => {
                if (a.id === assignment?.userId) return true;          // always show current assignee
                if (assignedUserIds.has(a.id)) return false;            // hide other already-assigned users
                if (!roleType) return true;                             // no role constraint → show all
                // Senior/management roles (exec_pa, admin, owner, accountant) appear in ALL slot
                // dropdowns regardless of the slot's own roleType — intentional, matching the
                // behaviour of exec_pa/admin/owner that predates the accountant addition.
                return a.roleType === roleType ||
                  (Array.isArray(a.roleTypes) && (a.roleTypes.includes('exec_pa') || a.roleTypes.includes('admin') || a.roleTypes.includes('owner') || a.roleTypes.includes('accountant'))) ||
                  a.roleType === 'exec_pa' ||
                  a.roleType === 'admin' ||
                  a.roleType === 'accountant';
              })
              .map((a: any) => {
                const lapse = lapsedInClaimState.get(a.id);
                const blocked = !!lapse && a.id !== assignment?.userId;
                return (
                  <SelectItem
                    key={a.id}
                    value={a.id}
                    disabled={blocked}
                    data-testid={blocked ? `option-license-lapsed-${a.id}` : undefined}
                    title={blocked ? `${adjusterDisplayName(a)} cannot be assigned: ${lapseText(lapse!)}. Save a renewed license on their profile to lift this.` : undefined}
                  >
                    {adjusterDisplayName(a)}
                    {lapse ? ` (${lapseText(lapse)})` : ""}
                  </SelectItem>
                );
              })}
          </SelectContent>
        </Select>

        {!assignment?.commissionRateRedacted && (
        <div className="flex items-center gap-0.5 shrink-0">
          <Input
            type="number"
            min="0"
            {...(currentFeeType === 'percent' ? { max: '100' } : {})}
            step={currentFeeType === 'percent' ? '0.1' : '0.01'}
            value={currentValue}
            disabled={readOnly}
            onChange={(e) => {
              if (assignment) setPercentageEdits((prev) => ({ ...prev, [assignment.id]: e.target.value }));
              else if (isExtraSlot) setNewSlotPercentages((prev) => ({ ...prev, [`${slotKey}-${slotIdx}`]: e.target.value }));
              else setNewSlotPercentages((prev) => ({ ...prev, [defaultSlotKey]: e.target.value }));
            }}
            onBlur={() => { if (assignment) handlePercentageCommit(assignment.id, role); }}
            onKeyDown={(e) => { if (e.key === "Enter" && assignment) handlePercentageCommit(assignment.id, role); }}
            className={`w-16 h-9 text-xs text-center bg-card dark:bg-muted px-1 ${
              currentFeeType === 'percent' && exceedsLimit ? "border-destructive focus-visible:ring-destructive" : ""
            }`}
            data-testid={assignment ? `${testId}-pct-${idx}` : `${testId}-new-pct-${slotIdx}`}
          />
          <Select
            value={currentFeeType}
            disabled={readOnly}
            onValueChange={(v) => {
              if (assignment) {
                setFeeTypeEdits((prev) => ({ ...prev, [assignment.id]: v }));
                setPercentageEdits((prev) => ({ ...prev, [assignment.id]: '0' }));
              } else if (isExtraSlot) {
                setNewSlotFeeTypes((prev) => ({ ...prev, [`${slotKey}-${slotIdx}`]: v }));
                setNewSlotPercentages((prev) => ({ ...prev, [`${slotKey}-${slotIdx}`]: '0' }));
              } else {
                setNewSlotFeeTypes((prev) => ({ ...prev, [defaultSlotKey]: v }));
                setNewSlotPercentages((prev) => ({ ...prev, [defaultSlotKey]: '0' }));
              }
            }}
          >
            <SelectTrigger className="w-14 h-9 text-xs bg-card dark:bg-muted px-1" data-testid={`${testId}-fee-type-${idx}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percent">%</SelectItem>
              <SelectItem value="flat_rate">$</SelectItem>
              <SelectItem value="hourly">$/hr</SelectItem>
            </SelectContent>
          </Select>
        </div>
        )}

        {canAssignClaims && (assignment || isExtraSlot) && (
          <Button
            size="icon"
            variant="ghost"
            type="button"
            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
            onClick={() => {
              if (isExtraSlot) {
                setExtraSlots((prev) => ({ ...prev, [slotKey]: Math.max(0, (prev[slotKey] || 0) - 1) }));
              } else if (assignment) {
                if (isFormMode) {
                   props.onAssignmentsChange(paAssignments.filter((a: any) => a.id !== assignment.id));
                } else {
                   if (confirm(`Remove this ${label} assignment?`)) {
                     props.deletePaAssignmentMutation.mutate(assignment.id);
                   }
                }
              }
            }}
            data-testid={isExtraSlot ? `btn-cancel-${role}-new-${slotIdx}` : `btn-remove-${role}-${idx}`}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className={`grid ${layout} gap-4`}>
        {activeRoles.map(({ role, label, testId, roleType }) => {
          const assignments = getPaAssignmentsByRole(role, label);
          const slotsCount = extraSlots[roleType || role] || 0;
          const isDynamic = addedRoleSections.some((r) => r.roleType === roleType);
          const customRoleName = !PA_ENUM_TYPES.has(roleType || role) ? label : undefined;

          return (
            <div key={`${role}-${roleType}`} className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-muted-foreground">{label}</Label>
                {canAssignClaims && isDynamic && assignments.length === 0 && (
                  <Button
                    size="icon"
                    variant="ghost"
                    type="button"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                    onClick={() => handleAddedRolesChange(addedRoleSections.filter((r) => r.roleType !== roleType))}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>

              {assignments.length > 0
                ? assignments.map((a: any, idx: number) =>
                    renderDropdownRow(a, role, label, testId, idx, false, undefined, roleType, customRoleName, !canAssignClaims),
                  )
                : renderDropdownRow(null, role, label, testId, 0, false, undefined, roleType, customRoleName, !canAssignClaims)}

              {canAssignClaims && Array.from({ length: slotsCount }).map((_, slotIdx) =>
                renderDropdownRow(null, role, label, testId, assignments.length + slotIdx, true, slotIdx, roleType, customRoleName),
              )}

              {canAssignClaims && (
                <Button
                  variant="ghost"
                  type="button"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground hover:text-foreground gap-1 px-1"
                  onClick={() => setExtraSlots((prev) => ({ ...prev, [roleType || role]: (prev[roleType || role] || 0) + 1 }))}
                  data-testid={`btn-add-more-${roleType || role}`}
                >
                  <Plus className="h-3 w-3" />
                  Add more
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {canAssignClaims && availableToAdd.length > 0 && (
        <div className="pt-1">
          <Select onValueChange={handleAddRoleSection} value="">
            <SelectTrigger className="w-full border-dashed text-muted-foreground h-9" data-testid="btn-add-role">
              <div className="flex items-center gap-1">
                <Plus className="h-3.5 w-3.5" />
                <span className="text-sm">Add Role</span>
              </div>
            </SelectTrigger>
            <SelectContent>
              {availableToAdd.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {lapsedAssignees.length > 0 && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive"
          data-testid="alert-license-lapsed"
        >
          <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            {lapsedAssignees.map((lapse) => (
              <p key={lapse.userId}>
                {lapse.adjusterName}'s {lapseText(lapse)}. They keep read access, but cannot be assigned to new claims in {lapse.state}.
                Reassign this claim, or save their renewed license on their profile.
              </p>
            ))}
          </div>
        </div>
      )}

      {exceedsLimit && (
        <p className="text-sm text-destructive font-medium">
          Total commission is {totalPercentage.toFixed(1)}% — cannot exceed {MAX_COMMISSION_PERCENTAGE}%
        </p>
      )}
    </div>
  );
}
